import type { AccessControlSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import '@adornis/charts/apexcharts/client/apex-chart.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { buildFullOptions, type FilterEditorOptions } from '@adornis/filter/filter-options.js';
import { consume } from '@lit/context';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { entityContextTokenProvider } from '../../util.js';
import { BHPageAdminGlobalStatistics } from './bh-page-admin-global-statistics.js';

/**
 * @element bh-page-admin-einrichtung-statistics
 */
@customElement('bh-page-admin-einrichtung-statistics')
export class BHPageAdminEinrichtungStatistics extends BHPageAdminGlobalStatistics {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @consume({ context: entityContextTokenProvider.getContext(AdminInstitutionViewSetting), subscribe: true })
  @state()
  protected readonly viewSettings?: { $instance: AdminInstitutionViewSetting };

  private _selectedTimeframe = new BehaviorSubject<string | null>(null);

  override readonly _filter = new RXController(
    this,
    combineLatest([
      this.institutionID.observable,
      this._selectedTimeframe.pipe(distinctUntilChanged()),
      this.refresh,
    ]).pipe(
      filter(([institutionID]) => !!institutionID),
      map(([institutionID, timeframeID]) => {
        const filter = this.filter as BHFilter;
        filter.institutionID = institutionID;
        filter.timeframeID = timeframeID;
        return filter;
      }),
    ),
  );

  override get filterKey() {
    return `statisics:${this.institutionID.value}:filter`;
  }

  override get filterOptions() {
    const filterOptions = buildFullOptions({
      fieldAspectGeneration: {
        class: Participation,
        shouldIncludeField: (baseQLFieldDefinition, path, baseEntity) => {
          const stammdatenPlugin = getPluginForScope('registration::stammdaten', this.institutionID.value ?? 'default');
          const stammdatenClass = stammdatenPlugin.classType();

          return shouldIncludeFieldHelpers.bySelectionSet({
            whitelist: {
              state: 1,
              isNoShow: 1,
              userStammdaten: stammdatenClass.allFields,
              product: {
                name: 1,
                category: { name: 1 },
              },
            } satisfies AccessControlSelectionSet<Participation>,
          })(baseQLFieldDefinition, path, baseEntity);
        },
      },
      filter: this.filter,
      onFilterChanged: filter => {
        this.setFilter(filter);
      },
    } satisfies FilterEditorOptions<typeof Participation>);

    filterOptions.searchable = false;

    return filterOptions;
  }

  override renderStats() {
    console.log(this.viewSettings);
    if (this.viewSettings?.$instance?.selectedTimeframeID)
      this._selectedTimeframe.next(this.viewSettings.$instance.selectedTimeframeID);

    return this._filter.value && this.institutionID.value
      ? getPluginForScope('institution-statistics', this.institutionID.value).render(this._filter.value)
      : html`<x-loader></x-loader>`;
  }
}
